import React, { useState } from "react";
import "./style.scss";
import "../Login/style.scss";
import useMyAccount from "./hooks/useMyAccount";
import { useDispatch, useSelector } from "react-redux";
import { State } from "../../store";
import { logOut } from "../../store/auth/authActions";
import { format } from "date-fns";
import { SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useFacility from "../../../utils/hooks/useFacility";
import useApiRequest from "../../../utils/hooks/useApiRequest";
import {
  USER_EDIT_DATA_URL,
  USER_CHANGE_PASS_URL,
  REMOVE_WEB_USER,
  RESIGN_FROM_NEWSLETTER,
  NEWSLETTER_AGREEMENT,
} from "../../api/apiRoutes";
import { setSuccessMessage } from "../../store/alerts/alertsActions";
import { USER_TOKEN } from "../../store/auth/authMiddleware";
import { emailValidator, phoneValidator, passValidator } from "../../../utils/formsValidation";
import FormInput from "../../../utils/FormInput";
import EAModal from "../shared/Modal/EAModal";
import ModalComponent from "../../../features/ModalComponent";
import EAAlert from "../shared/EAAlert";
import QRCode from "react-qr-code";
import getDelphiNumber from "../../../utils/getDelphiNumber";

type EditDataInputs = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
};

type ChangePasswordInputs = {
  oldPassword: string;
  newPassword: string;
};

const MyAccount: React.FC = () => {
  useMyAccount();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const facilityIds = useFacility();
  const { post } = useApiRequest();
  const userToken = sessionStorage.getItem(USER_TOKEN);

  const userData = useSelector((state: State) => state.auth.userData);

  const [openEditData, setOpenEditData] = useState(false);
  const [openChangePassword, setOpenChangePassword] = useState(false);
  const [openRemoveAccount, setOpenRemoveAccount] = useState(false);
  const [openNewsletterModal, setOpenNewsletterModal] = useState(false);
  const [passwordError, setPasswordError] = useState<string>("");

  // Form for edit data
  const {
    register: registerEdit,
    handleSubmit: handleSubmitEdit,
    formState: { errors: errorsEdit },
  } = useForm<EditDataInputs>({
    defaultValues: {
      firstName: userData.firstName,
      lastName: userData.lastName,
      email: userData.email,
      phone: userData.phone,
    },
  });

  // Form for change password
  const {
    register: registerPassword,
    handleSubmit: handleSubmitPassword,
    formState: { errors: errorsPassword },
  } = useForm<ChangePasswordInputs>();

  const handleEditUserData: SubmitHandler<EditDataInputs> = async ({ firstName, lastName, email, phone }) => {
    const { isError } = await post(USER_EDIT_DATA_URL(facilityIds), { ...userData, firstName, lastName, email, phone });
    if (!isError) {
      dispatch(setSuccessMessage(t("operacja_przebiegla_pomyslnie")));
      setOpenEditData(false);
    }
  };

  const handleChangePassword: SubmitHandler<ChangePasswordInputs> = async (data) => {
    setPasswordError("");
    const parsedUserToken = userToken ? JSON.parse(userToken).sessionToken : "";
    const url = USER_CHANGE_PASS_URL(facilityIds);
    const body = {
      oldpassword: data.oldPassword,
      password: data.newPassword,
      token: parsedUserToken,
    };
    const { isError, errorTable } = await post(url, body);

    if (!isError) {
      dispatch(setSuccessMessage(t("operacja_przebiegla_pomyslnie")));
      setOpenChangePassword(false);
      setPasswordError("");
    } else {
      const errorMessage = errorTable && errorTable.length > 0 ? errorTable[0] : t("wystapil_blad");
      setPasswordError(errorMessage);
    }
  };

  const handleRemoveAccount = async () => {
    const { isError } = await post(REMOVE_WEB_USER(facilityIds));
    if (!isError) {
      dispatch(logOut());
      dispatch(setSuccessMessage("Usunięto konto użytkownika"));
    }
  };

  const handleNewsletterToggle = async () => {
    if (userData.allowMailing) {
      // Wypisz się z newslettera
      const { isError } = await post(RESIGN_FROM_NEWSLETTER(facilityIds, userData.email));
      if (!isError) {
        dispatch(setSuccessMessage("Wypisano z newslettera"));
        setOpenNewsletterModal(false);
      }
    } else {
      // Zapisz się na newsletter
      const { isError } = await post(NEWSLETTER_AGREEMENT(facilityIds, userData.email));
      if (!isError) {
        dispatch(setSuccessMessage("Wysłano email z potwierdzeniem zapisu do newslettera"));
        setOpenNewsletterModal(false);
      }
    }
  };

  const getCardId = () => {
    if (userData.account?.objectId) {
      return `-${String(userData.account.objectId)}`;
    }
   
  };

  const getValidityDate = () => {
    if (userData.closeDate) {
      return format(userData.closeDate, "dd.MM.yyyy");
    }
    return "Brak daty";
  };

  const getBirthDate = () => {
    if (userData.birthDay && userData.birthDay !== "NULL" && userData.birthDay !== "") {
      try {
        // Format z backendu: yyyy-MM-dd (np. "1990-07-15")
        const [year, month, day] = userData.birthDay.split("-");

        const months = [
          "stycznia",
          "lutego",
          "marca",
          "kwietnia",
          "maja",
          "czerwca",
          "lipca",
          "sierpnia",
          "września",
          "października",
          "listopada",
          "grudnia",
        ];

        const monthName = months[parseInt(month) - 1];
        return `${parseInt(day)} ${monthName}`;
      } catch {
        return "Brak daty";
      }
    }
    return "Brak daty";
  };

  return (
    <div className="account-wrap">
      <div className="blob b1"></div>
      <div className="blob b2"></div>
      <div className="wave"></div>

      <header className="account-top">
        <div className="account-logo">Klub Przyjaciół Term Uniejów — Moje konto</div>
        <button className="btn-logout" onClick={() => dispatch(logOut())}>
          Wyloguj
        </button>
      </header>

      <div className="account-grid">
        {/* LEWA — Karta Klubu */}
        <section className="member-card">
          <h3>Karta członkowska — Klub Przyjaciół Term Uniejów</h3>
          <p className="hint">Pokaż tę kartę przy wejściu lub zeskanuj QR w kasie.</p>

          <div className="card-ui">
            {/* <div className="card-top">
              <span className="club-tag">KLUB PRZYJACIÓŁ TERM</span>
              <span className="status-tag">STATUS: Aktywny</span>
            </div> */}

            <div className="card-user">
              <div>
              <h4>
                {userData.firstName} {userData.lastName}
              </h4>
              <p className="card-id">ID: {`EA_${getDelphiNumber(userData.account)}`}</p>
              <p className="card-birth">
                Urodziny: <b>{getBirthDate()}</b>
              </p>
              {/* <p className="card-valid">
                Karta do: <b>{getValidityDate()}</b>
              </p> */}
</div>
                    <div className="qr flex-center-center flex-column">
                      <QRCode value={`EA_${getDelphiNumber(userData.account)}`} size={100} />
                      <b>{`EA_${getDelphiNumber(userData.account)}`}</b>
                    </div>
            </div>

            <div className="card-dot"></div>
          </div>
        </section>

        {/* PRAWA — Dane Konta */}
        <aside className="account-details">
          <h3>Dane konta</h3>

          <div className="info-box">
            <div className="label">Imię i nazwisko</div>
            <div className="value">
              {userData.firstName} {userData.lastName}
            </div>
          </div>

          <div className="info-box">
            <div className="label">E-mail</div>
            <div className="value">{userData.email}</div>
          </div>

          <div className="info-box">
            <div className="label">Telefon</div>
            <div className="value">{userData.phone || "Brak"}</div>
          </div>

          <div className="info-box">
            <div className="label">Urodziny</div>
            <div className="value">{getBirthDate()}</div>
          </div>

          <div className="info-box">
            <div className="label">Newsletter</div>
            <div className="value">{userData.allowMailing ? "Zasubskrybowany" : "Nieaktywny"}</div>
            {/* <button className="mini-btn" onClick={() => setOpenNewsletterModal(true)}>
              Zarządzaj
            </button> */}
          </div>

          <h3 className="quick-title">Szybkie akcje</h3>
          <div className="quick-actions">
            <button className="btn-primary" onClick={() => setOpenEditData(true)}>
              Edytuj dane
            </button>
            <button className="btn-primary" onClick={() => setOpenChangePassword(true)}>
              Zmień hasło
            </button>
            <button className="btn-danger" onClick={() => setOpenRemoveAccount(true)}>
              Usuń konto
            </button>
          </div>
        </aside>
      </div>

      {/* Modal - Edytuj dane */}
      {openEditData && (
        <EAModal
          isOpen={openEditData}
          onClose={() => setOpenEditData(false)}
          title={t("dane_konta")}
          acceptButtonLabel={t("zapisz")}
          onAccept={handleSubmitEdit(handleEditUserData)}
        >
          <form onSubmit={handleSubmitEdit(handleEditUserData)}>
            <FormInput
              type="text"
              name="firstName"
              text={t("imie")}
              register={{ ...registerEdit("firstName", { required: true }) }}
              errors={errorsEdit}
              isLabel
              defaultValue={userData.firstName}
            />
            <FormInput
              type="text"
              name="lastName"
              text={t("nazwisko")}
              register={{ ...registerEdit("lastName", { required: true }) }}
              errors={errorsEdit}
              isLabel
              defaultValue={userData.lastName}
            />
            <FormInput
              type="text"
              name="email"
              text="E-mail"
              register={{ ...registerEdit("email", { ...emailValidator(true) }) }}
              errors={errorsEdit}
              isLabel
              defaultValue={userData.email}
            />
            <FormInput
              type="number"
              name="phone"
              text={t("telefon_kontaktowy")}
              register={{ ...registerEdit("phone", { ...phoneValidator(true) }) }}
              errors={errorsEdit}
              isLabel
              defaultValue={userData.phone}
            />
          </form>
        </EAModal>
      )}

      {/* Modal - Zmień hasło */}
      {openChangePassword && (
        <EAModal
          isOpen={openChangePassword}
          onClose={() => {
            setOpenChangePassword(false);
            setPasswordError("");
          }}
          title={t("zmiana_hasla")}
          acceptButtonLabel={t("zmien")}
          onAccept={handleSubmitPassword(handleChangePassword)}
        >
          <form onSubmit={handleSubmitPassword(handleChangePassword)}>
            <FormInput
              type="password"
              name="oldPassword"
              text={t("stare_haslo")}
              register={{ ...registerPassword("oldPassword", {}) }}
              errors={errorsPassword}
              isLabel
            />
            <FormInput
              type="password"
              name="newPassword"
              text={t("nowe_haslo")}
              register={{ ...registerPassword("newPassword", { ...passValidator(true) }) }}
              errors={errorsPassword}
              isLabel
            />
            {passwordError && (
              <div style={{ marginTop: "16px" }}>
                <EAAlert message={passwordError} type="error" />
              </div>
            )}
          </form>
        </EAModal>
      )}

      {/* Modal - Usuń konto */}
      {openRemoveAccount && (
        <ModalComponent
          open
          footer={true}
          closeModal={() => setOpenRemoveAccount(false)}
          title={t("czy_na_pewno_chcesz_usnac_swoj_konto")}
          onAccept={handleRemoveAccount}
        >
          <></>
        </ModalComponent>
      )}

      {/* Modal - Zarządzaj newsletterem */}
      {openNewsletterModal && (
        <ModalComponent
          open
          footer={true}
          closeModal={() => setOpenNewsletterModal(false)}
          title={userData.allowMailing ? "Wypisz się z newslettera" : "Zapisz się na newsletter"}
          onAccept={handleNewsletterToggle}
        >
          <p>
            {userData.allowMailing
              ? "Czy na pewno chcesz zrezygnować z otrzymywania newslettera?"
              : "Czy chcesz zapisać się na newsletter? Otrzymasz email z potwierdzeniem."}
          </p>
        </ModalComponent>
      )}
    </div>
  );
};

export default MyAccount;
